import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Container,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    useMediaQuery,
    useTheme,
    colors,
    Badge,
    Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { openShoppingList } from '../../store/slices/shoppingListSlice';
import { semanticColors, themeColors } from '../../theme/colors';
import ShoppingListModal from '../ShoppingList/ShoppingListModal';

const Header: React.FC = () => {
    const dispatch = useAppDispatch();
    const { items: shoppingListItems } = useAppSelector((state) => state.shoppingList);
    const [openMap, setOpenMap] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleOpenMap = () => {
        setOpenMap(true);
        setDrawerOpen(false); // закрыть drawer если открыт
    };

    const handleCloseMap = () => setOpenMap(false);

    const handleOpenShoppingList = () => {
        dispatch(openShoppingList());
        setDrawerOpen(false); // закрыть drawer если открыт
    };

    const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);

    const menuItems = [
        // {
        //     label: 'Склады',
        //     action: handleOpenMap,
        // },
        {
            label: 'Интернет-магазин',
            href: 'https://skynet-service.ru',
        },
        {
            label: `Список заказа (${shoppingListItems.length})`,
            action: handleOpenShoppingList,
            badge: shoppingListItems.length,
        },
    ];

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: semanticColors.glass.background,
                    backdropFilter: 'blur(20px)',
                    borderBottom: `1px solid ${semanticColors.border.light}`,
                }}
            >
                <Container maxWidth={false}>
                    <Toolbar disableGutters sx={{ height: 60, width: '100%' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                px: 2,
                            }}
                        >
                            <Box
                                component="img"
                                src="/image/logo-skynet.png"
                                alt="SkyNet"
                                sx={{ width: 160, height: 'auto' }}
                            />

                            {isMobile ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* Кнопка списка заказа на мобильных */}
                                    <Tooltip title="Список заказа">
                                        <IconButton
                                            onClick={handleOpenShoppingList}
                                            sx={{
                                                color: themeColors['600'],
                                                '&:hover': {
                                                    backgroundColor: themeColors['50'],
                                                },
                                            }}
                                        >
                                            <Badge badgeContent={shoppingListItems.length} color="primary">
                                                <ShoppingCartIcon />
                                            </Badge>
                                        </IconButton>
                                    </Tooltip>

                                    <IconButton onClick={toggleDrawer(true)}>
                                        <MenuIcon />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>

                                    {menuItems.slice(0, 2).map((item) =>
                                        item.href ? (
                                            <Button
                                                key={item.label}
                                                href={item.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    color: colors.cyan[900],
                                                }}
                                            >
                                                {item.label}
                                            </Button>
                                        ) : (
                                            <Button
                                                key={item.label}
                                                onClick={item.action}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    color: colors.cyan[900],
                                                }}
                                            >
                                                {item.label}
                                            </Button>
                                        )
                                    )}
                                    {/* Кнопка списка заказа на десктопе */}
                                    <Tooltip title="Список заказа">
                                        <IconButton
                                            onClick={handleOpenShoppingList}
                                            sx={{
                                                color: themeColors['600'],
                                                '&:hover': {
                                                    backgroundColor: themeColors['50'],
                                                },
                                            }}
                                        >
                                            <Badge badgeContent={shoppingListItems.length} color="primary">
                                                <ShoppingCartIcon />
                                            </Badge>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Drawer для мобильного меню */}
            <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
                <Box sx={{ width: 250, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box
                            component="img"
                            src="/image/logo-skynet.png"
                            alt="SkyNet"
                            sx={{ width: 120, height: 'auto' }}
                        />
                        <IconButton onClick={toggleDrawer(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <List>
                        {menuItems.map((item) => (
                            <ListItem key={item.label} disablePadding>
                                {item.href ? (
                                    <ListItemButton
                                        component="a"
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <ListItemText primary={item.label} />
                                    </ListItemButton>
                                ) : (
                                    <ListItemButton onClick={item.action}>
                                        <ListItemText
                                            primary={item.label}
                                            primaryTypographyProps={{
                                                sx: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }
                                            }}
                                        />
                                        {item.badge !== undefined && item.badge > 0 && (
                                            <Badge badgeContent={item.badge} color="primary">
                                                <ShoppingCartIcon fontSize="small" />
                                            </Badge>
                                        )}
                                    </ListItemButton>
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer>

            {/* Модалка со складами */}
            <Dialog
                open={openMap}
                onClose={handleCloseMap}
                fullWidth
                maxWidth="lg"
                sx={{
                    "&> .MuiDialog-container": {
                        "&>.MuiPaper-root": {
                            background: '#fff'
                        }
                    }
                }}
            >
                <DialogTitle sx={{ background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Наши склады
                    <IconButton onClick={handleCloseMap} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ height: '500px', width: '100%', }}>
                        Здесь будет карта со складами.
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Модальное окно списка заказа */}
            <ShoppingListModal />
        </>
    );
};

export default Header;